import { createFileRoute, Link, notFound } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"

import { BackgroundCheckSection } from "@/components/company/background-check-section"
import { BuyerBewareSection } from "@/components/company/buyer-beware-section"
import { CompanyHeader } from "@/components/company/company-header"
import { CompanyMapLazy } from "@/components/company/company-map-lazy"
import { CompanyNav } from "@/components/company/company-nav"
import { CompanyTrustSidebar } from "@/components/company/company-trust-sidebar"
import { HowWeGotScoreSection } from "@/components/company/how-we-got-score-section"
import { LongTermSatisfactionSection } from "@/components/company/long-term-satisfaction-section"
import { PerformanceByWorkTypeSection } from "@/components/company/performance-by-work-type-section"
import { PlatformList } from "@/components/company/platform-list"
import { ReviewHistorySection } from "@/components/company/review-history-section"
import { TopInstallersGeoSwap } from "@/components/company/top-installers-geo-swap"
import { TopInstallersSection } from "@/components/company/top-installers-section"
import { VerdictSection } from "@/components/company/verdict-section"
import { WhatYouCanExpectSection } from "@/components/company/what-you-can-expect-section"
import { SharedFooter } from "@/components/shared-footer"
import { WBSection } from "@/components/wb-ui/wb-section"
import {
  CompanySection,
  dataByCompanySection,
  ORDERED_COMPANY_SECTIONS,
} from "@/constants/company/company-section"
import type { State } from "@/constants/state"
import { dataByState, stateSlugByCode } from "@/constants/state"
import { getCountByDeceptionType } from "@/helpers/company/buyer-beware"
import { getCustomerGroupData } from "@/helpers/company/customer-group-metrics"
import {
  getReviewsWithData,
  getValidReviews,
  hasParaphrasedReviewText,
} from "@/helpers/company/review-utils"
import { getWorkTypeMetrics } from "@/helpers/company/work-type-metrics"
import { selectWhatYouCanExpectReviewsByPhase } from "@/helpers/company/wyce-review-selection"
import {
  calculateSentimentByPhaseId,
  calculateSignalCountByPhaseId,
} from "@/helpers/company/wyce-sentiment"
import { formatRankDisplay } from "@/helpers/market/format-rank-display"
import { parseVerdictOutput, type VerdictOutput } from "@/types/models"

type CompanyPageOverride = {
  updatedAt: string
  verdict: Pick<VerdictOutput, "narrative" | "verdict">
  reviewParaphraseById: Record<string, string>
}

const companyPageOverrideByLongSlug: Record<string, CompanyPageOverride> = {
  byltup: {
    updatedAt: "2026-08-04T12:00:00.000Z",
    verdict: {
      narrative:
        "BYLTup handles roofing with polish and brings proven solar experience to combined projects. Reviews on this page describe completed GAF Timberline Solar shingle installations and combined reroof-and-solar projects. Customer feedback is strongly positive on workmanship and post-sale support, although a small number of reviews describe support problems. We found particularly strong patterns in emergency response and warranty-minded project management. After storms, the team showed up the same day, and one reviewer said BYLTup recommended maintenance instead of an unnecessary reroof, saving thousands. Solar still represents a smaller share of its public review history than roofing, but the evidence supports treating BYLTup as a roofing-and-solar contractor, not a roofer merely considering solar.",
      verdict:
        "If you need a roof fixed fast or want roofing and solar coordinated by one contractor, BYLTup is worth the call. Its solar track record is smaller than its roofing record, but customers describe completed solar projects, including Timberline Solar installations.",
    },
    reviewParaphraseById: {
      "abea70f4-8f77-4a5f-8e81-c249143fae04":
        "Nicole N. described the BYLTup team as fantastic. They finished on time and made sure she was totally satisfied, which she said was not an easy task with some members of her family.",
    },
  },
}

const fetchCompanyPageData = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => data as { longSlug: string })
  // eslint-disable-next-line complexity -- The loader's parallel data assembly is deliberately local to this route.
  .handler(async ({ data }) => {
    const { sql } = await import("drizzle-orm")
    const { fetchByLongSlugWithMethodologyAndMarket } =
      await import("@/db/queries/company")
    const { getLocationsForDisplayByCompanyId } =
      await import("@/db/queries/company-location")
    const { fetchTopCompaniesForMarket } = await import("@/db/queries/market")
    const { fetchReviewsWithSignalsForCompanyId } =
      await import("@/db/queries/parsed-review")
    const { getSourcesWithLatestProfile } = await import("@/db/queries/source")
    const { db } = await import("@/lib/admin-db")

    const { longSlug } = data

    const companyData = await fetchByLongSlugWithMethodologyAndMarket(longSlug)

    // A NULL qualification is unqualified, as on the contact page.
    if (companyData?.is_page_qualified !== true) return null

    const {
      id,
      name,
      company_status,
      methodology_output,
      absolute_rank,
      pool_size,
      market_name,
      market_slug,
      market_level,
      updated_at,
      state_code,
      should_index: shouldIndex,
    } = companyData

    const isOutOfBusiness = company_status === "out_of_business"
    const rankDisplay =
      absolute_rank == null
        ? null
        : formatRankDisplay(
            absolute_rank,
            pool_size,
            methodology_output.trustScore
          )
    const rankLabel =
      isOutOfBusiness || !rankDisplay
        ? null
        : `${rankDisplay} ${market_level === "national" ? "nationally" : `in ${market_name}`}`

    const [
      sources,
      reviewsWithSignals,
      locationsWithReviews,
      verdictResult,
      topInstallers,
    ] = await Promise.all([
      getSourcesWithLatestProfile({ companyIds: [id] }),
      fetchReviewsWithSignalsForCompanyId(id),
      getLocationsForDisplayByCompanyId(id),
      db.execute(sql`
        SELECT output
        FROM company_prompt_run
        WHERE company_id = ${id}
          AND type = 'verdict'
        ORDER BY created_at DESC
        LIMIT 1
      `),
      market_slug && state_code
        ? fetchTopCompaniesForMarket({
            stateCode: state_code,
            marketSlug: market_slug,
            excludeLongSlug: longSlug,
            limit: 5,
          })
        : Promise.resolve(null),
    ])

    const latestVerdict = verdictResult[0] as { output: unknown } | undefined
    const parsedVerdictOutput = latestVerdict
      ? parseVerdictOutput(latestVerdict.output)
      : null
    const companyPageOverride = companyPageOverrideByLongSlug[longSlug]
    const verdictOverride = companyPageOverride?.verdict
    const verdictOutput = verdictOverride
      ? {
          topReviewIds: parsedVerdictOutput?.topReviewIds ?? [],
          ...verdictOverride,
        }
      : parsedVerdictOutput

    const reviewsForDisplay = companyPageOverride
      ? reviewsWithSignals.map((review) => {
          const paraphraseOverride =
            companyPageOverride.reviewParaphraseById[review.id]
          return paraphraseOverride
            ? { ...review, paraphrased_text: paraphraseOverride }
            : review
        })
      : reviewsWithSignals

    const reviewsWithPageData = getReviewsWithData(
      reviewsForDisplay,
      methodology_output
    )

    // Resolve top reviews directly from verdict-provided review IDs
    const topReviews: typeof reviewsWithPageData = []
    if (verdictOutput?.topReviewIds) {
      const reviewById: Record<string, (typeof reviewsWithPageData)[number]> =
        {}
      for (const r of reviewsWithPageData) reviewById[r.id] = r
      for (const reviewId of verdictOutput.topReviewIds) {
        const review = reviewById[reviewId]
        if (review && hasParaphrasedReviewText(review)) topReviews.push(review)
      }
    }

    // Pre-compute all section data server-side so raw review arrays
    // never cross the server→client boundary
    const deceptionCounts = getCountByDeceptionType(reviewsWithSignals)
    const validReviews = getValidReviews(reviewsWithSignals)
    const workTypeMetrics = getWorkTypeMetrics(validReviews, methodology_output)
    const customerGroupData = getCustomerGroupData(
      validReviews,
      methodology_output
    )
    const sentimentByPhaseId = calculateSentimentByPhaseId(
      methodology_output.workQualityBySignal
    )
    const signalCountByPhaseId = calculateSignalCountByPhaseId(
      methodology_output.workQualityBySignal
    )
    const reviewsByPhaseId = selectWhatYouCanExpectReviewsByPhase({
      reviews: reviewsForDisplay,
      methodologyOutput: methodology_output,
      canIncludeReview: (review) => Boolean(review.paraphrased_text?.trim()),
      sentimentByPhaseId,
    })

    // Per-platform review counts from methodology data (matches methodology total)
    const reviewCountByPlatform: Record<string, number> = {}
    for (const r of reviewsWithSignals) {
      if (r.platform) {
        reviewCountByPlatform[r.platform] =
          (reviewCountByPlatform[r.platform] ?? 0) + 1
      }
    }

    return {
      longSlug,
      name,
      company_status,
      methodology_output,
      updated_at: companyPageOverride?.updatedAt ?? updated_at,
      state_code,
      market_name,
      market_slug,
      market_level,
      shouldIndex,
      isOutOfBusiness,
      rankLabel,
      sources: sources.map(({ platform, profile }) => ({
        platform,
        reviewSummary: profile.review_summary
          ? {
              averageRating: profile.review_summary.avg_rating,
              reviewCount: profile.review_summary.num_reviews,
            }
          : null,
      })),
      reviewsWithSignals,
      locationsWithReviews,
      verdictOutput,
      topReviews,
      topInstallers,
      deceptionCounts,
      workTypeMetrics,
      customerGroupData,
      sentimentByPhaseId,
      signalCountByPhaseId,
      reviewsByPhaseId,
      reviewCountByPlatform,
    }
  })

export const Route = createFileRoute("/company/$longSlug/")({
  loader: async ({ params }) => {
    const result = await fetchCompanyPageData({
      data: { longSlug: params.longSlug },
    })
    if (!result) throw notFound()
    return result
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const { name, longSlug, shouldIndex } = loaderData
    const title = `${name} Reviews`
    const description = `Read verified customer reviews for ${name}. See trust scores, ratings, and what real solar customers say.`
    const url = `https://www.wattbot.com/company/${longSlug}`
    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "robots",
          content: shouldIndex ? "index, follow" : "noindex, follow",
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: "/og-images/og-reports.png" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: "/og-images/og-reports.png" },
      ],
      links: [{ rel: "canonical", href: url }],
    }
  },
  component: CompanyPage,
})

// eslint-disable-next-line complexity -- This legacy route keeps its ordered page sections in one component.
function CompanyPage() {
  const {
    longSlug,
    name,
    methodology_output,
    updated_at,
    state_code,
    market_name,
    market_slug,
    market_level,
    isOutOfBusiness,
    rankLabel,
    sources,
    reviewsWithSignals,
    locationsWithReviews,
    verdictOutput,
    topReviews,
    topInstallers,
    deceptionCounts,
    workTypeMetrics,
    customerGroupData,
    sentimentByPhaseId,
    signalCountByPhaseId,
    reviewsByPhaseId,
    reviewCountByPlatform,
  } = Route.useLoaderData()

  const sectionData = {
    methodologyOutput: methodology_output,
    deceptionCounts,
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://www.wattbot.com/company/${longSlug}`,
    name,
    url: `https://www.wattbot.com/company/${longSlug}`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: Math.round((methodology_output.trustScore / 20) * 10) / 10,
      bestRating: "5",
      worstRating: "1",
      reviewCount: methodology_output.reviewCounts.total,
    },
    review: {
      "@type": "Review",
      author: {
        "@type": "Organization",
        name: "WattBot",
        url: "https://wattbot.com",
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: methodology_output.trustScore,
        bestRating: "100",
        worstRating: "1",
      },
      ...(verdictOutput?.narrative
        ? {
            reviewBody:
              verdictOutput.narrative.length > 200
                ? verdictOutput.narrative.slice(
                    0,
                    verdictOutput.narrative.lastIndexOf(" ", 200)
                  ) + "..."
                : verdictOutput.narrative,
          }
        : {}),
    },
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Outer wrapper - gray background with padding */}
      <div className="bg-muted min-h-screen p-2 sm:p-6 lg:p-8">
        {/* Outer grid - sidebar sits outside the inner frame */}
        <div className="mx-auto max-w-6xl pt-16 lg:grid lg:grid-cols-12 lg:gap-6 lg:pt-0">
          {/* Inner frame - white background with border (content only) */}
          <div className="bg-background min-h-[calc(100vh-4rem)] border-2 lg:col-span-9">
            {/* Mobile sticky header */}
            <div className="lg:hidden">
              <CompanyTrustSidebar
                companyName={name}
                companyLongSlug={longSlug}
                trustScore={methodology_output.trustScore}
                rankLabel={rankLabel}
                isOutOfBusiness={isOutOfBusiness}
              />
            </div>

            {/* Out of Business Warning */}
            {isOutOfBusiness && (
              <div className="bg-bad text-bad-foreground border-b-2 py-4 text-center text-sm font-semibold">
                This company appears to be out of business
              </div>
            )}

            {/* Black Header Bar */}
            <CompanyHeader name={name} updatedAt={updated_at} />

            {/* Hero Section */}
            <WBSection className="border-b-2 py-8 lg:py-12">
              {/* Category Badge + Location */}
              <div className="mb-4 flex items-center gap-3">
                {[state_code, market_name].some(Boolean) && (
                  <span className="wb-label-muted">
                    {state_code === "US" ? (
                      "NATIONAL"
                    ) : (
                      <>
                        {state_code &&
                          dataByState[state_code as State]?.name.toUpperCase()}
                        {state_code && market_name && " / "}
                        {market_name && market_slug && state_code ? (
                          <Link
                            to="/solar-installers/$state/$marketSlug/$"
                            params={{
                              state: stateSlugByCode[state_code],
                              marketSlug: market_slug,
                              _splat: "",
                            }}
                            className="underline"
                          >
                            {market_name.toUpperCase()}
                          </Link>
                        ) : (
                          market_name?.toUpperCase()
                        )}
                      </>
                    )}
                  </span>
                )}
              </div>

              {/* Company Name */}
              <div className="wb-h1 mb-6 max-w-2xl">{name}</div>

              {/* Subtext */}
              <div className="wb-data-muted border-muted-foreground mt-6 inline-block border-t pt-4">
                {methodology_output.reviewCounts.total.toLocaleString()} Reviews
                • {locationsWithReviews.length} Location
                {locationsWithReviews.length === 1 ? "" : "s"} •{" "}
                {(methodology_output.reviewCounts.total * 133).toLocaleString()}{" "}
                Data Points Processed
              </div>
            </WBSection>

            {/* Navigation Tabs */}
            <CompanyNav className="w-full border-b-2" />

            {/* Main Content */}
            <div>
              {/* Service Area Map */}
              <div className="relative h-[300px] w-full overflow-hidden border-b-2 lg:h-[400px]">
                <CompanyMapLazy
                  companyLocations={locationsWithReviews}
                  companySlug={longSlug}
                />
              </div>

              {/* Main Sections */}
              {ORDERED_COMPANY_SECTIONS.map((sectionId) => {
                const config = dataByCompanySection[sectionId]
                let content: React.ReactNode = null
                switch (sectionId) {
                  case CompanySection.REVIEW_HISTORY:
                    content = (
                      <ReviewHistorySection reviews={reviewsWithSignals} />
                    )
                    break
                  case CompanySection.WHAT_STOOD_OUT:
                    content = (
                      <VerdictSection
                        verdictOutput={verdictOutput}
                        topReviews={topReviews}
                      />
                    )
                    break
                  case CompanySection.PLATFORMS_MONITORED:
                    content = (
                      <PlatformList
                        sources={sources}
                        reviewCountByPlatform={reviewCountByPlatform}
                      />
                    )
                    break
                  case CompanySection.HOW_WE_GOT_SCORE:
                    content = <HowWeGotScoreSection />
                    break
                  case CompanySection.WHAT_YOU_CAN_EXPECT:
                    content = (
                      <WhatYouCanExpectSection
                        sentimentByPhaseId={sentimentByPhaseId}
                        signalCountByPhaseId={signalCountByPhaseId}
                        reviewsByPhaseId={reviewsByPhaseId}
                      />
                    )
                    break
                  case CompanySection.PERFORMANCE_BY_WORK_TYPE:
                    content = (
                      <PerformanceByWorkTypeSection
                        workTypeMetrics={workTypeMetrics}
                      />
                    )
                    break
                  case CompanySection.LONG_TERM_SATISFACTION:
                    content = (
                      <LongTermSatisfactionSection
                        customerGroupData={customerGroupData}
                        companyName={name}
                      />
                    )
                    break
                  case CompanySection.BACKGROUND_CHECK:
                    content = (
                      <BackgroundCheckSection
                        characterBreakdown={
                          methodology_output.companyCharacter.breakdown
                        }
                      />
                    )
                    break
                  case CompanySection.BUYER_BEWARE:
                    content = (
                      <BuyerBewareSection deceptionCounts={deceptionCounts} />
                    )
                    break
                }

                return (
                  <WBSection
                    key={sectionId}
                    id={sectionId}
                    title={config.getTitle(sectionData)}
                  >
                    {content}
                  </WBSection>
                )
              })}
            </div>

            {topInstallers &&
              topInstallers.companies.length > 0 &&
              (market_level === "national" ? (
                <TopInstallersGeoSwap
                  companyLongSlug={longSlug}
                  fallbackTitle="Top Solar Installers Nationwide"
                  fallbackCompanies={topInstallers.companies}
                  fallbackMarket={{
                    name: "",
                    poolSize: topInstallers.poolSize,
                    pageHref: null,
                  }}
                />
              ) : (
                <TopInstallersSection
                  title={`Top Solar Installers in ${market_name}`}
                  companies={topInstallers.companies}
                  market={{
                    name: market_name ?? "",
                    poolSize: topInstallers.poolSize,
                    pageHref:
                      state_code && market_slug
                        ? `/solar-installers/${stateSlugByCode[state_code]}/${market_slug}`
                        : null,
                  }}
                />
              ))}

            <SharedFooter />
          </div>

          {/* Desktop Sidebar - outside the inner frame */}
          <div className="sticky top-4 hidden lg:col-span-3 lg:block lg:self-start">
            <CompanyTrustSidebar
              companyName={name}
              companyLongSlug={longSlug}
              trustScore={methodology_output.trustScore}
              rankLabel={rankLabel}
              isOutOfBusiness={isOutOfBusiness}
            />
          </div>
        </div>
      </div>
    </>
  )
}
